import { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { grantTargets, rootCatalogNodes } from "@equipped/core/content/grantTargets";
import { buildPermalink, type PermalinkTarget } from "@equipped/core/permalink/permalink";
import { qrMatrix } from "@equipped/core/qr/qrMatrix";
import { qrMatrixToSvg } from "@equipped/core/qr/qrSvg";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";
import { ADMIN_STORE_LINKS_CONFIG } from "../config/adminDeployment.config";
import { publicContentBaseUrl } from "../lib/contentBase";
import { downloadQrPng, downloadQrSvg } from "../lib/qrDownload";
import { useCatalogTree } from "../lib/useCatalogTree";

type AppLinkAction = "catalog" | "provider";
type Platform = "ios" | "android";
type CopyState = "idle" | "copied" | "failed";

const PERMALINK_OPTIONS = { carrier: "scheme", scheme: "beequipped" } as const;
const QR_RENDER_OPTIONS = { moduleSize: 8, quietZone: 4 } as const;

/** Mirrors the Worker's `catalogUrlForGrant` inverse of core's `deriveGrantRef`. */
function catalogUrlFor(baseUrl: string, ref: string): string {
  return `${baseUrl.replace(/\/+$/, "")}/${ref.replace(/^\/+|\/+$/g, "")}/catalog.json`;
}

function withInviteCode(providerLink: string, inviteCode: string): string {
  const code = inviteCode.trim();
  if (!code) return providerLink;
  const link = new URL(providerLink);
  // Same parameter name and normalization as worker/src/inviteEmail.ts's
  // buildInviteAppLink: `invite=<trimmed uppercase code>`.
  link.searchParams.set("invite", code.toUpperCase());
  return link.toString();
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [state, setState] = useState<CopyState>("idle");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
    } catch {
      setState("failed");
    }
    window.setTimeout(() => setState("idle"), 2000);
  };

  return (
    <Button
      variant="outlined"
      startIcon={<ContentCopyOutlinedIcon />}
      onClick={copy}
      disabled={!value}
    >
      {state === "copied" ? "Copied!" : state === "failed" ? "Couldn’t copy" : label}
    </Button>
  );
}

function DeepLinkQr({ value }: { value: string }) {
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const matrix = useMemo(() => qrMatrix(value), [value]);
  const svgMarkup = useMemo(() => qrMatrixToSvg(matrix, QR_RENDER_OPTIONS), [matrix]);

  const downloadPng = () => {
    setDownloadError(null);
    void downloadQrPng(matrix, "app-link-qr.png", QR_RENDER_OPTIONS).catch((error: unknown) => {
      setDownloadError(error instanceof Error ? error.message : "Could not download PNG");
    });
  };

  return (
    <Stack sx={{ gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "fit-content",
          p: 2,
          bgcolor: "#fff",
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
        // The link is encoded into a boolean QR matrix; it is never interpolated into
        // the SVG markup. This is the same safe render path as UtilitiesPage.
        dangerouslySetInnerHTML={{ __html: svgMarkup }}
      />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={downloadPng}>
          Download PNG
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadOutlinedIcon />}
          onClick={() => downloadQrSvg(svgMarkup, "app-link-qr.svg")}
        >
          Download SVG
        </Button>
      </Box>
      {downloadError ? <Alert severity="error">{downloadError}</Alert> : null}
    </Stack>
  );
}

/** docs/390 — handout links for installing the app and opening a catalog/provider in it. */
export function AppLinksPage() {
  const { backendUrl } = useAuth();
  const [action, setAction] = useState<AppLinkAction>("catalog");
  const [platform, setPlatform] = useState<Platform>("ios");
  const [selectedCatalogRef, setSelectedCatalogRef] = useState("");
  const [inviteCode, setInviteCode] = useState("");

  const contentBaseUrl = publicContentBaseUrl(backendUrl);
  const { tree, loading: treeLoading, error: treeError } = useCatalogTree(backendUrl);
  const catalogTargets = useMemo(() => {
    const roots = rootCatalogNodes(tree, contentBaseUrl).map(({ target }) => target);
    const descendants = grantTargets(tree, contentBaseUrl).filter((target) => target.kind === "catalog");
    const seen = new Set<string>();
    return [...roots, ...descendants].filter((target) => {
      if (seen.has(target.ref)) return false;
      seen.add(target.ref);
      return true;
    });
  }, [contentBaseUrl, tree]);

  const activeCatalogRef = catalogTargets.some((target) => target.ref === selectedCatalogRef)
    ? selectedCatalogRef
    : (catalogTargets[0]?.ref ?? "");
  const selectedCatalogUrl = activeCatalogRef
    ? catalogUrlFor(contentBaseUrl, activeCatalogRef)
    : "";

  const deepLink = useMemo(() => {
    let target: PermalinkTarget | null = null;
    if (action === "catalog" && selectedCatalogUrl) {
      target = { kind: "catalog", url: selectedCatalogUrl };
    } else if (action === "provider" && backendUrl) {
      target = { kind: "provider", providerUrl: backendUrl };
    }
    if (!target) return "";
    const link = buildPermalink(target, PERMALINK_OPTIONS);
    return action === "provider" ? withInviteCode(link, inviteCode) : link;
  }, [action, backendUrl, inviteCode, selectedCatalogUrl]);

  const iosDownloadUrl = ADMIN_STORE_LINKS_CONFIG.iosStoreUrl || ADMIN_STORE_LINKS_CONFIG.iosTestFlightUrl;
  const downloadUrl = platform === "ios" ? iosDownloadUrl : ADMIN_STORE_LINKS_CONFIG.androidStoreUrl;
  const downloadLabel = platform === "ios"
    ? ADMIN_STORE_LINKS_CONFIG.iosStoreUrl ? "iOS App Store link" : "iOS TestFlight link"
    : "Android store link";
  const missingDownloadConfig = platform === "ios"
    ? "Not configured yet — set VITE_IOS_STORE_URL (or VITE_IOS_TESTFLIGHT_URL)."
    : "Not configured yet — set VITE_ANDROID_STORE_URL.";

  return (
    <Stack sx={{ gap: 3 }}>
      <MainCard title="App Links & QR">
        <Stack sx={{ gap: 3 }}>
          <Typography sx={{ color: "text.secondary" }}>
            Build the two links for a handout: one installs Be Equipped, and one opens the app at a specific destination.
          </Typography>

          <Box sx={{ display: "grid", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Action
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={action}
              onChange={(_event, value: AppLinkAction | null) => value && setAction(value)}
              aria-label="App link action"
            >
              <ToggleButton value="catalog">Add a catalog</ToggleButton>
              <ToggleButton value="provider">Connect a provider</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: "grid", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Target
            </Typography>
            {action === "catalog" ? (
              <TextField
                select
                fullWidth
                label="Catalog"
                value={activeCatalogRef}
                disabled={treeLoading || catalogTargets.length === 0}
                onChange={(event) => setSelectedCatalogRef(event.target.value)}
                helperText={
                  treeLoading
                    ? "Loading catalogs…"
                    : treeError
                      ? `Couldn’t load catalogs: ${treeError}`
                      : catalogTargets.length === 0
                        ? "No published catalogs are available."
                        : selectedCatalogUrl
                }
              >
                {catalogTargets.map((target) => (
                  <MenuItem key={target.ref} value={target.ref}>
                    {target.path}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Stack sx={{ gap: 2 }}>
                <TextField
                  fullWidth
                  label="Provider base URL"
                  value={backendUrl ?? ""}
                  helperText="Uses the provider currently selected in this admin."
                  slotProps={{ input: { readOnly: true } }}
                />
                <TextField
                  fullWidth
                  label="Invite code (optional)"
                  value={inviteCode}
                  onChange={(event) => setInviteCode(event.target.value)}
                  helperText="Appended as the existing invite parameter; codes are normalized to uppercase."
                  slotProps={{ htmlInput: { maxLength: 128 } }}
                />
                <Alert severity="warning">
                  Requires an app version newer than v0.1.0 (release version TBD): the current app parses provider links,
                  but its native-intent opener does not yet route them to Add Provider. Do not hand out this provider link
                  until that app update ships.
                </Alert>
              </Stack>
            )}
          </Box>

          <Box sx={{ display: "grid", gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Platform for the download link
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={platform}
              onChange={(_event, value: Platform | null) => value && setPlatform(value)}
              aria-label="Download platform"
            >
              <ToggleButton value="ios">iOS</ToggleButton>
              <ToggleButton value="android">Android</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Stack>
      </MainCard>

      <MainCard title="Deep link">
        {deepLink ? (
          <Stack sx={{ gap: 2 }}>
            <TextField
              fullWidth
              label="Platform-agnostic app link"
              value={deepLink}
              slotProps={{ input: { readOnly: true, sx: { fontFamily: "monospace" } } }}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <CopyButton value={deepLink} label="Copy deep link" />
            </Box>
            <DeepLinkQr value={deepLink} />
          </Stack>
        ) : (
          <Alert severity="info">
            {action === "catalog"
              ? "Choose an available catalog to generate its deep link."
              : "Configure or select a provider backend URL to generate its deep link."}
          </Alert>
        )}
      </MainCard>

      <MainCard title="Download link">
        {downloadUrl ? (
          <Stack sx={{ gap: 2 }}>
            <TextField
              fullWidth
              label={downloadLabel}
              value={downloadUrl}
              slotProps={{ input: { readOnly: true, sx: { fontFamily: "monospace" } } }}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <CopyButton value={downloadUrl} label="Copy download link" />
            </Box>
          </Stack>
        ) : (
          <Alert severity="warning">{missingDownloadConfig}</Alert>
        )}
      </MainCard>

      <Alert severity="info">
        Send both — the download link installs the app, the deep link (or its QR) opens it to this catalog/provider.
        A single smart link that auto-routes installed-vs-not needs a hosted page (docs/163 v1) and is a follow-up.
      </Alert>
    </Stack>
  );
}
